/**
 * Wallet rules for credentials the server withheld and for a company whose business verification
 * badge a re-check with the provider withdrew.
 */
import { credentialTrustMessageId, credentialTrustStateOf } from '../../util/credentialTrust';

/**
 * The wallet's own state for a document whose response failed the browser's signature check.
 * Its details are never shown.
 */
export const CREDENTIAL_CHECK_FAILED = 'credential_check_failed';

/** The English message id for a vault document's trust state, or null for none. */
export const vaultDocTrustMessageId = state =>
  state === CREDENTIAL_CHECK_FAILED
    ? 'CredentialTrust.checkFailed'
    : credentialTrustMessageId(state);

const lower = value => (typeof value === 'string' ? value.toLowerCase() : '');

/**
 * Whether a verification record is a business verification. Same rule the server uses when it
 * files the record: a `kyb` document type, or a name that says KYB or business registry.
 */
export const isBusinessVerificationRecord = record => {
  if (!record) return false;
  const name = lower(record.documentName);
  return (
    lower(record.documentType).startsWith('kyb') ||
    name.includes('kyb') ||
    name.includes('business registry')
  );
};

const createdAt = record => {
  const time = new Date(record.createdDate).getTime();
  return Number.isNaN(time) ? -Infinity : time;
};

/**
 * The withdrawal the company still has to act on, or null.
 *
 * Only the newest business verification record counts: once the company starts a new business
 * verification, that newer record carries no withdrawal and the notice goes away. A withdrawal the
 * provider later reversed is marked `reinstated` and counts as none.
 *
 * @param {Array<Object>} records – the owner's own verification records
 * @returns {{freeReverificationAvailable: boolean}|null}
 */
export const businessWithdrawalOf = records => {
  if (!Array.isArray(records)) return null;
  const newest = records
    .filter(isBusinessVerificationRecord)
    .reduce((latest, record) => (!latest || createdAt(record) > createdAt(latest) ? record : latest), null);
  if (newest?.recheck?.outcome !== 'withdrawn') return null;
  return {
    freeReverificationAvailable: newest.recheck.freeReverification?.status === 'available',
  };
};

/** The export's failed entries, each with the trust state it reports (or null). */
export const withTrustStates = failed =>
  (Array.isArray(failed) ? failed : []).map(entry => ({
    ...entry,
    trustState: credentialTrustStateOf(entry),
  }));
